import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface ApprovalRequest {
  id: string;
  tenant_id: string | null;
  clinic_id: string | null;
  requested_by: string;
  reviewed_by: string | null;
  action_type: string;
  action_sensitivity: 'standard' | 'elevated' | 'critical';
  target_table: string;
  target_record_id: string;
  justification: string;
  rejection_reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  action_payload: Record<string, unknown> | null;
  created_at: string;
  reviewed_at: string | null;
  expires_at: string | null;
}

/**
 * Hook to fetch pending approval requests for owners/managers
 */
export const usePendingApprovals = (clinicId?: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["approval-requests", "pending", clinicId],
    queryFn: async () => {
      let query = supabase
        .from("approval_requests")
        .select("*")
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (clinicId) {
        query = query.eq("clinic_id", clinicId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ApprovalRequest[];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // Refresh every 30s for time-sensitive approvals
  });
};

/**
 * Hook to fetch the current user's submitted approval requests
 */
export const useMyApprovalRequests = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["approval-requests", "mine", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("approval_requests")
        .select("*")
        .eq("requested_by", user!.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as ApprovalRequest[];
    },
    enabled: !!user?.id,
  });
};

/**
 * Hook to submit a new approval request (for managers performing critical actions)
 */
export const useSubmitApprovalRequest = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      clinicId: string;
      tenantId?: string;
      actionType: string;
      targetTable: string;
      targetRecordId: string;
      justification: string;
      actionPayload?: Record<string, Json>;
    }) => {
      const { data, error } = await supabase
        .from("approval_requests")
        .insert({
          clinic_id: params.clinicId,
          tenant_id: params.tenantId || null,
          requested_by: user!.id,
          action_type: params.actionType,
          action_sensitivity: 'critical',
          target_table: params.targetTable,
          target_record_id: params.targetRecordId,
          justification: params.justification,
          action_payload: params.actionPayload || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-requests"] });
      toast.success("Approval request submitted. The owner will be notified.");
    },
    onError: (error: Error) => {
      toast.error(`Failed to submit request: ${error.message}`);
    },
  });
};

/**
 * Hook to approve or reject a pending request (for owners)
 */
export const useReviewApprovalRequest = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      requestId: string;
      decision: 'approved' | 'rejected';
      rejectionReason?: string;
    }) => {
      // Read the request first so the action can run BEFORE the status flips.
      // Executing after the update would leave a request marked "approved"
      // whose action never happened if execution failed — the same silent
      // divergence that hid the broken discount approvals for months.
      const { data: request, error: readError } = await supabase
        .from("approval_requests")
        .select("*")
        .eq("id", params.requestId)
        .single();

      if (readError) throw readError;

      if (params.decision === 'approved' && request.action_payload) {
        await executeApprovedAction(request as ApprovalRequest);
      }

      const { data, error } = await supabase
        .from("approval_requests")
        .update({
          status: params.decision,
          reviewed_by: user!.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: params.rejectionReason || null,
        })
        .eq("id", params.requestId)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        throw new Error(
          "Review was not saved — only the tenant owner can approve or reject requests.",
        );
      }

      // Log to audit trail
      await supabase.from("action_audit_log").insert({
        clinic_id: data.clinic_id,
        tenant_id: data.tenant_id,
        user_id: user!.id,
        action_type: `review_${params.decision}`,
        action_sensitivity: 'critical',
        target_table: 'approval_requests',
        target_record_id: params.requestId,
        details: { original_action: data.action_type, decision: params.decision },
        approval_request_id: params.requestId,
      });

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["approval-requests"] });
      queryClient.invalidateQueries({ queryKey: ["patient-bills"] });
      toast.success(
        variables.decision === 'approved'
          ? "Request approved. Action has been executed."
          : "Request rejected."
      );
    },
    onError: (error: Error) => {
      toast.error(`Failed to review request: ${error.message}`);
    },
  });
};

/**
 * Execute the approved action (e.g., void a bill)
 */
async function executeApprovedAction(request: ApprovalRequest) {
  const payload = request.action_payload as Record<string, unknown>;

  switch (request.action_type) {
    case 'void_bill': {
      // Runs in the approving owner's session, so process_bill_void's ceiling
      // is satisfied by their authority. The RPC returns any money still held
      // (via the requested refund method) before voiding — a raw header update
      // here would strand the patient's money on a cancelled bill.
      const { error } = await (supabase.rpc as any)("process_bill_void", {
        p_bill_id: request.target_record_id,
        p_void_reason: (payload?.reason as string) || request.justification,
        p_refund_method: (payload?.refund_method as string) ?? null,
        p_void_category: (payload?.void_category as string) ?? null,
      });
      if (error) throw error;
      break;
    }
    case 'apply_discount': {
      await supabase
        .from("patient_bills")
        .update({
          discount_percentage: payload?.discount_percentage as number,
          discount_amount: payload?.discount_amount as number,
        })
        .eq("id", request.target_record_id);
      break;
    }
    case 'write_off': {
      await supabase
        .from("patient_bills")
        .update({
          payment_status: 'void',
          void_reason: `Write-off: ${payload?.reason || 'Approved by owner'}`,
        })
        .eq("id", request.target_record_id);
      break;
    }
    case 'refund_bill': {
      // Runs in the approving owner's session, so process_bill_refund's
      // amount ceiling is satisfied by the owner's own authority — the
      // request row carries the terms, not the permission.
      const { error } = await (supabase.rpc as any)("process_bill_refund", {
        p_bill_id: request.target_record_id,
        p_refund_amount: Number(payload?.refund_amount ?? 0),
        p_refund_reason: (payload?.refund_reason as string) || request.justification,
        p_refund_method: (payload?.refund_method as string) || 'cash',
        p_restore_wallet: Number(payload?.restore_wallet ?? 0),
      });
      if (error) throw error;
      break;
    }
    default:
      console.warn(`Unknown action type: ${request.action_type}`);
  }
}
